import {
  Product,
  PurchaseErrorEnum,
  QuoteState,
  type CipherText,
  type CompleteInteractiveAuthRequest,
  type FulfillRequest,
  type ProductOpaqueValue,
  type PurchaseError,
} from "@b2bportal/purchase-api";
import type { State } from "xstate";
import { getChildState, getParentState } from "../../../helpers";
import { CartFulfillState, ParentState } from "../../../types";
import { ActivitySelectors } from "../../Products/Activities";
import { AirDisruptionSelectorsV1 } from "../../Products/AirDisruptionV1";
import { cartQuoteSelectors } from "../CartQuote";
import type { CartContext } from "../context";
import { CartState } from "../types";

type CartFulfillStateType = State<CartContext>;
type CartFulfillStateWithoutValue = Pick<CartFulfillStateType, "context">;

const getPrepareFulfillmentDetails = (
  state: CartFulfillStateWithoutValue
): ProductOpaqueValue[] | undefined => {
  const details: ProductOpaqueValue[] = [];

  // Use type assertion since CartContext doesn't know about activities-specific context
  const context = state.context as unknown as Record<string, unknown>;
  const activitiesShop = context[ParentState.activitiesShop] as
    | { activityId?: string }
    | undefined;
  const hasActivitiesContext = activitiesShop?.activityId != null;

  if (hasActivitiesContext) {
    const activitiesPrepare =
      ActivitySelectors.getActivitiesPrepareFulfillmentDetails(state as any);
    if (activitiesPrepare) {
      details.push(activitiesPrepare);
    }
  }

  if (AirDisruptionSelectorsV1.hasAirDisruptionInQuote(state as any)) {
    const disruptionPrepare =
      AirDisruptionSelectorsV1.getAirDisruptionPrepareFulfillmentDetails(
        state as any
      );
    if (disruptionPrepare) {
      details.push(disruptionPrepare);
    }
  }

  return details.length > 0 ? details : undefined;
};

export const getFullFillRequestParams = (
  state: CartFulfillStateWithoutValue
): FulfillRequest => {
  const quoteCipherToken = cartQuoteSelectors.getPriceQuoteCipherText(state);
  const payments = getFulfillRequestPayments(state);

  const quoteBalance = cartQuoteSelectors.getRemainingBalance(state);
  const quoteState = cartQuoteSelectors.getQuoteBreakdown(state).state;
  const isFulfillable =
    quoteBalance.fiat.value === 0 && quoteState === QuoteState.Fulfillable;

  const prepareFulfillmentDetails = getPrepareFulfillmentDetails(state);

  return {
    quoteToken: quoteCipherToken as CipherText,
    payments: isFulfillable ? [] : payments,
    ...(prepareFulfillmentDetails && { prepareFulfillmentDetails }),
  };
};

export const getFulfillCipherText = (state: CartFulfillStateWithoutValue) =>
  state.context[CartState.cartFulfill]?.fulfillCipherText;

export const getOpenCartFulfillModal = (state: CartFulfillStateType) =>
  (getChildState(state.value) as CartFulfillState) !== CartFulfillState.error &&
  Object.keys(state.value).includes(ParentState.cartFulfill);

export const getOpenCartFulfillErrorModal = (state: CartFulfillStateType) =>
  getParentState(state.value) === ParentState.cartFulfill &&
  getChildState(state.value) === CartFulfillState.error;

export const getCartFulfillError = (state: CartFulfillStateType) =>
  state.context[CartState.cartFulfill].error;

export const getCartFulfillUserActions = (state: CartFulfillStateType) =>
  state.context[CartState.cartFulfill].userAction;

export const getCartFulfillFailedAt = (state: CartFulfillStateType) =>
  state.context[CartState.cartFulfill].error?.failedAt;

export const getCartFulfillErrorFailureReasons = (
  state: CartFulfillStateType
) => {
  const error = getCartFulfillError(state);

  if (!error) return;

  const errors =
    error.data && Array.isArray(error.data)
      ? (error.data as PurchaseError[])
      : [];
  return errors?.map((error: PurchaseError) =>
    error.Error === PurchaseErrorEnum.ErrorCode
      ? error.code
      : error.Error || "ProductError"
  );
};

export const getFulfillRequestPayments = (
  state: CartFulfillStateType | CartFulfillStateWithoutValue
) => state.context[CartState.cartFulfill]?.fulfillRequestPayments;

export const getFulfillPayments = (
  state: CartFulfillStateType | CartFulfillStateWithoutValue
) => state.context[CartState.cartFulfill]?.fulfilledPayments;

export const getFulfilledProducts = (
  state: CartFulfillStateType | CartFulfillStateWithoutValue
) => state.context[CartState.cartFulfill]?.fulfilledProducts;

// Products the provider declined at fulfillment (excluded from `fulfilledProducts`,
// kept in the order at no charge). Empty for the vast majority of orders.
export const getNotPurchasedProducts = (
  state: CartFulfillStateType | CartFulfillStateWithoutValue
) => state.context[CartState.cartFulfill]?.notPurchasedProducts;

export const hasNotPurchasedRbcInsuranceProduct = (
  state: CartFulfillStateType | CartFulfillStateWithoutValue
): boolean =>
  !!getNotPurchasedProducts(state)?.some(
    (p) => p.type === Product.RbcInsurance
  );

// This is the customer-facing reservation code (e.g. XVJ2MJQW5TNT)
export const getFulfilledProductsReservationId = (
  state: CartFulfillStateType | CartFulfillStateWithoutValue
) => getFulfilledProducts(state)?.[0]?.value?.customerReservationId;

// This is a UUID used internally to fetch reservations
export const getFulfilledProductsReservationBookingId = (
  state: CartFulfillStateType | CartFulfillStateWithoutValue
) => getFulfilledProducts(state)?.[0]?.value?.reservationBookingId;

// This is a booking code provided by the lodging provider (e.g. EAN)
export const getFulfilledProductsProviderBookingId = (
  state: CartFulfillStateType | CartFulfillStateWithoutValue
) => getFulfilledProducts(state)?.[0]?.value?.providerBookingId;

export const getCartFulfilledLodgingBooking = (
  state: CartFulfillStateType | CartFulfillStateWithoutValue
) => {
  const products = getFulfilledProducts(state);
  const lodgingProduct = products?.find((p) => p.type === Product.Hotel);
  return lodgingProduct?.value;
};

export const getCartFulfillTrackingProperties = (
  state: CartFulfillStateType | CartFulfillStateWithoutValue
) => state.context.cartFulfill?.trackingProperties;

//TODO: Move selectors
export const hasFulfilledAirCfarProduct = (
  state: CartFulfillStateWithoutValue
): boolean => {
  const products = getFulfilledProducts(state);
  return !!products?.find((p) => p.type === Product.AirCfar);
};

export const hasFulfilledDisruptionProduct = (
  state: CartFulfillStateWithoutValue
): boolean => {
  const products = getFulfilledProducts(state);
  return !!products?.find((p) => p.type === Product.AirDisruption);
};

export const hasFulfilledAirChfarProduct = (
  state: CartFulfillStateWithoutValue
): boolean => {
  const products = getFulfilledProducts(state);
  return !!products?.find((p) => p.type === Product.AirChfar);
};

export const getCartFulfillCallState = (state: CartFulfillStateWithoutValue) =>
  state.context[CartState.cartFulfill].callState;

// 3DS Selectors
export const getCartFulfillPendingInteractive = (state: CartFulfillStateType) =>
  state.context[CartState.cartFulfill].pendingInteractive;

export const getCartFulfillCompleteInteractive = (
  state: CartFulfillStateType | CartFulfillStateWithoutValue
) => state.context[CartState.cartFulfill].completeInteractive;

export const getCompleteInteractiveFulFillRequestParams = (
  state: CartFulfillStateWithoutValue
): CompleteInteractiveAuthRequest => {
  const token = getFulfillCipherText(state);
  const completeInteractive = getCartFulfillCompleteInteractive(state);

  return {
    token,
    ...completeInteractive,
  };
};
